import { useEffect } from "react";

import { isSpecialImageType } from "Components/ServiceIcon/imageType";
import { objectEntries } from "Libs/utils";
import {
  loadDeploymentCurrent,
  loadDeploymentNext,
  nextDeploymentSelector,
  currentDeploymentSelector,
  nextDeploymentErrorSelector,
  currentDeploymentErrorSelector
} from "Reducers/environment/deployment";
import { useAppDispatch, useAppSelector } from "Store/hooks";

import type { Deployment } from "Api/organizations/projects/environments/deployments/types";
import type {
  LoadDeploymentCurrentProps,
  LoadDeploymentNextProps
} from "Reducers/environment/deployment";

const addClassAndIcon = <T extends Record<string, { type: string }>>(
  service: T,
  kind: "routes" | "app" | "service" | "worker",
  specialIconsEnabled: boolean = true
) =>
  objectEntries(service).reduce(
    (current, [key, value]) => {
      let icon = value?.type?.split(":")[0];
      if (!specialIconsEnabled && isSpecialImageType(icon)) {
        icon = "placeholder";
      }
      current[key] = { name: key, icon, class: kind, kind, ...value };
      return current;
    },
    {} as Record<
      keyof T,
      {
        name: string | number | symbol;
        icon: string;
        class: string;
        kind: "routes" | "app" | "service" | "worker";
      } & T[keyof T]
    >
  );

export const getDeploymentServices = (
  deployment?: Deployment,
  includeRoutes = true,
  specialIconsEnabled: boolean = true
) => {
  if (!deployment) {
    return;
  }

  return Object.assign(
    includeRoutes
      ? {
          routes: addClassAndIcon(
            deployment.routes,
            "routes",
            specialIconsEnabled
          )
        }
      : {},
    addClassAndIcon(deployment.webapps, "app", specialIconsEnabled),
    addClassAndIcon(deployment.services, "service", specialIconsEnabled),
    addClassAndIcon(deployment.workers, "worker", specialIconsEnabled)
  );
};

/**
 * This hook utilizes the loadDeploymentCurrent thunk, supports a dispatch condition, and returns relevant deployment data
 * @param deployment
 * @param shouldDispatch
 * @returns
 */

export const useLoadDeploymentsCurrent = (
  deployment: LoadDeploymentCurrentProps,
  shouldDispatch: boolean | undefined = true
) => {
  const dispatch = useAppDispatch();
  const { organizationId, projectId, environmentId, settings } = deployment;

  const currentDeployment = useAppSelector(state =>
    currentDeploymentSelector(state, { ...deployment })
  );
  const currentDeploymentError = useAppSelector(state =>
    currentDeploymentErrorSelector(state, { ...deployment })
  );

  useEffect(() => {
    if (shouldDispatch) {
      dispatch(
        loadDeploymentCurrent({
          organizationId,
          projectId,
          environmentId,
          settings
        })
      );
    }
  }, [
    dispatch,
    organizationId,
    projectId,
    environmentId,
    shouldDispatch,
    settings
  ]);

  return {
    currentDeployment,
    currentDeploymentError
  };
};

/**
 * This hook utilizes the  loadDeploymentNext thunk, supports a dispatch condition, and returns relevant deployment data
 * @param deployment
 * @param shouldDispatch
 * @returns
 */

export const useLoadDeploymentsNext = (
  deployment: LoadDeploymentNextProps,
  shouldDispatch: boolean | undefined = true
) => {
  const dispatch = useAppDispatch();
  const { organizationId, projectId, environmentId } = deployment;

  const nextDeployment = useAppSelector(state =>
    nextDeploymentSelector(state, { ...deployment })
  );
  const nextDeploymentError = useAppSelector(state =>
    nextDeploymentErrorSelector(state, { ...deployment })
  );

  useEffect(() => {
    if (shouldDispatch) {
      dispatch(
        loadDeploymentNext({
          organizationId,
          projectId,
          environmentId
        })
      );
    }
  }, [dispatch, organizationId, projectId, environmentId, shouldDispatch]);

  return {
    nextDeployment,
    nextDeploymentError
  };
};
